import { Box, Typography } from '@mui/material';
import ModalityChip from '~/components/Chip/ModalityChip';
import { Link } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ModelDetails from '~/components/ModelDetails';
import ImageProperties from '~/components/Properties/ImageProperties';
import { useObjectDetailsDialog } from '~/components/Properties/ObjectDetailsDialog';
import { ObjectRow } from '~/components/Properties/ObjectInfoButton';
import PropertiesTooltip from '~/components/Properties/PropertiesTooltip';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Image3D } from '~/domain/patient/image3d';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { formatSeriesDate } from '~/utils/date';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';
import { formatMmAsCmAtScale } from '~/utils/units';
import ImageBadges from './ImageBadges';
import ImageInfoButton from './ImageInfoButton';

const LINK_ICON_SIZE = 10;

interface ImageItemProps {
    image: Image3D;
    isSecondary?: boolean;
    hasSharedFrameOfReference?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
    onRename?: () => void;
    onDelete?: () => void;
}

export default function ImageItem({
    image,
    isSecondary,
    hasSharedFrameOfReference,
    isSelected,
    onSelect,
    onRename,
    onDelete,
}: ImageItemProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    const metadata = useMemo(() => transform3DImageData(image), [image]);

    const description =
        metadata.series.description ||
        intl.formatMessage({ id: 'unknown.series' });

    const sliceSpacingCm = formatMmAsCmAtScale(
        metadata.pixelSpacing[2],
        'voxel'
    );
    // Renaming shows the series description in clear and edits it, so it
    // is not offered while patient info is hidden.
    const rename = showSensitiveInfo ? onRename : undefined;
    const dialog = useObjectDetailsDialog();
    const infoButton = (
        <ImageInfoButton
            image={image}
            dialog={dialog}
            hasSharedFrameOfReference={hasSharedFrameOfReference}
            onRename={onRename}
            onDelete={onDelete}
        />
    );
    const content = (
        <PropertiesTooltip
            title={
                <ImageProperties
                    image={image}
                    hasSharedFrameOfReference={hasSharedFrameOfReference}
                    onViewDicom={
                        image.firstSlice?.datasetWrapper
                            ? () => dialog.open('tags')
                            : undefined
                    }
                    onRename={rename}
                    onDelete={onDelete}
                />
            }
        >
            <Box display="grid">
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    width="100%"
                    overflow="hidden"
                >
                    {isSecondary && (
                        <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                        >
                            <Link size={LINK_ICON_SIZE} color="gray" />
                        </Box>
                    )}
                    <ModalityChip modality={metadata.modality} size="small" />
                    <Typography
                        fontSize={12}
                        noWrap
                        flex={1}
                        minWidth={0}
                        title={showSensitiveInfo ? description : undefined}
                        color={
                            isSelected || isSelected === undefined
                                ? 'text.primary'
                                : 'text.secondary'
                        }
                    >
                        <SensitiveInfoTypography>
                            {description}
                        </SensitiveInfoTypography>
                    </Typography>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        flexShrink={0}
                    >
                        <ImageBadges
                            image={image}
                            hasSharedFrameOfReference={
                                hasSharedFrameOfReference
                            }
                        />
                        <Typography
                            fontSize={10}
                            color="text.secondary"
                            whiteSpace="nowrap"
                        >
                            {intl.formatMessage(
                                { id: 'image.sliceCount' },
                                { count: metadata.length }
                            )}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    width="100%"
                    overflow="hidden"
                >
                    <Typography
                        fontSize={10}
                        color="text.secondary"
                        whiteSpace="nowrap"
                        flexShrink={0}
                    >
                        <SensitiveInfoTypography>
                            {formatSeriesDate(metadata.series.date) ||
                                'Unknown date'}
                        </SensitiveInfoTypography>
                    </Typography>
                    <Typography fontSize={10} color="text.secondary" noWrap>
                        {intl.formatMessage(
                            { id: 'image.sliceSpacing' },
                            { value: sliceSpacingCm }
                        )}
                    </Typography>
                </Box>
                <ModelDetails
                    fullWidth
                    generationModel={metadata.generationModel}
                />
            </Box>
        </PropertiesTooltip>
    );

    const row = (
        <ObjectRow
            info={infoButton}
            data-testid={`image-item-${metadata.series.id}`}
        >
            {content}
        </ObjectRow>
    );

    if (onSelect === undefined && isSelected === undefined) {
        return row;
    }

    return (
        <SelectableListItem isSelected={isSelected} onClick={onSelect}>
            {row}
        </SelectableListItem>
    );
}
